import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from '../../lib/utils';
import dayjs from 'dayjs';
import { useMemberTickets } from '@/features/groupTicket/hooks';
import { useMemberBookings } from '@/features/groupBooking/hooks';
import { MemberTicket } from '@/features/groupTicket/types';
import { MemberBooking } from '@/features/groupBooking/types';
import ModalMembershipCreate from '../MemberProfile/ModalMembershipCreate';
import ModalGroupTicketSessionRegister from '../MemberProfile/ModalGroupTicketSessionRegister';
import { CalendarPlus, ExternalLink } from 'lucide-react';
import MembershipPauseBadge from '../MembershipPause/MembershipPauseBadge';
import { getPauseBadge } from '@/features/membershipPause/status';

const STATUS_CFG: Record<string, { label: string; bg: string; color: string }> = {
    ACTIVE: { label: '활성', bg: 'rgba(16,185,129,0.18)', color: '#065F46' },
    EXPIRED: { label: '만료', bg: 'rgba(156,163,175,0.22)', color: '#374151' },
    SUSPENDED: { label: '정지', bg: 'rgba(251,191,36,0.22)', color: '#92400E' },
    REFUNDED: { label: '환불', bg: 'rgba(239,68,68,0.18)', color: '#991B1B' },
};

const BOOKING_STATUS_CFG: Record<string, { label: string; bg: string; color: string }> = {
    CONFIRMED: { label: '예약', bg: '#EFF6FF', color: '#1E40AF' },
    COMPLETED: { label: '출석', bg: '#D1FAE5', color: '#065F46' },
    NOSHOW: { label: '노쇼', bg: '#FEE2E2', color: '#991B1B' },
    CANCELLED: { label: '취소', bg: '#F3F4F6', color: '#6B7280' },
    WAITLISTED: { label: '대기', bg: '#FEF3C7', color: '#92400E' },
};

const thStyle: React.CSSProperties = { padding: '10px 14px', textAlign: 'left', fontWeight: 500, color: '#6B7280', fontSize: 12 };
const tdStyle: React.CSSProperties = { padding: '10px 14px', color: '#374151', fontSize: 13 };

const MemberGroupTab = ({
    centerId,
    memberId,
    memberName,
    createAlert,
    isReadOnly = false,
}: {
    centerId: string;
    memberId: string;
    memberName?: string;
    createAlert: any;
    isReadOnly?: boolean;
}) => {
    const navigate = useNavigate();
    // 공유받은 그룹 수강권도 함께 본다. 참여자 프로필에서 0건으로 보이던 문제.
    const { data: memberTicketsRaw = [], isLoading: ticketsLoading } = useMemberTickets({
        centerId,
        memberId,
        includeShared: true,
    });

    // PT 수강권 정렬과 동일 정책: 먼저 써야 하는 카드부터 표시
    //   1) ACTIVE > SUSPENDED > EXPIRED > REFUNDED 순
    //   2) 같은 상태 내에서는 만료일 오름차순 (임박 → 여유)
    const STATUS_ORDER: Record<string, number> = { ACTIVE: 0, SUSPENDED: 1, EXPIRED: 2, REFUNDED: 3 };
    const memberTickets = [...memberTicketsRaw].sort((a, b) => {
        const orderDiff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
        if (orderDiff !== 0) return orderDiff;
        const aExp = a.expiryDate ? dayjs(a.expiryDate).valueOf() : Infinity;
        const bExp = b.expiryDate ? dayjs(b.expiryDate).valueOf() : Infinity;
        return aExp - bExp;
    });
    const activeTicketCount = memberTickets.filter((ticket) => ticket.status === 'ACTIVE').length;
    const { data: bookingsData, isLoading: bookingsLoading } = useMemberBookings({ memberId, status: 'all', limit: 50 });
    const bookings: MemberBooking[] = bookingsData?.results ?? [];

    const [showIssue, setShowIssue] = useState(false);
    const [registerTarget, setRegisterTarget] = useState<MemberTicket | null>(null);
    const [bookingTab, setBookingTab] = useState<'upcoming' | 'past'>('upcoming');
    const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>({});
    const [hideCancelled, setHideCancelled] = useState(false);
    const [hoveredRegisterTicketId, setHoveredRegisterTicketId] = useState<string | null>(null);
    const [activeRegisterTicketId, setActiveRegisterTicketId] = useState<string | null>(null);

    const toggleMonth = (month: string) => {
        setExpandedMonths(prev => ({
            ...prev,
            [month]: prev[month] !== undefined ? !prev[month] : false, // 기본값(true)에서 토글되므로 undefined면 false로
        }));
    };

    const isMonthExpanded = (month: string, idx: number) => {
        if (expandedMonths[month] !== undefined) return expandedMonths[month];
        return idx === 0; // 기본적으로 첫 번째(최신) 달만 펼침
    };

    // 예약 시간을 기준으로 내림차순 정렬 (최신순)
    const sortedBookings = [...bookings].sort((a, b) => dayjs(b.startTime).valueOf() - dayjs(a.startTime).valueOf());

    const filteredBookings = hideCancelled ? sortedBookings.filter(b => b.status !== 'CANCELLED') : sortedBookings;

    const now = dayjs();
    const upcomingBookings = filteredBookings.filter(b => dayjs(b.startTime).isAfter(now) || dayjs(b.startTime).isSame(now, 'minute'));
    const pastBookings = filteredBookings.filter(b => dayjs(b.startTime).isBefore(now, 'minute'));
    
    const displayedBookings = bookingTab === 'upcoming' ? upcomingBookings : pastBookings;

    // 날짜별(월별) 그룹화
    const groupedBookings = displayedBookings.reduce((acc, current) => {
        const monthKey = dayjs(current.startTime).format('YYYY년 M월');
        if (!acc[monthKey]) acc[monthKey] = [];
        acc[monthKey].push(current);
        return acc;
    }, {} as Record<string, typeof displayedBookings>);

    // MemberTicket의 이름 — 서버가 ticketId를 populate해서 반환 (ticketId.name)
    // 레거시: 저장 시점에 남아있는 ticketName 필드 fallback
    const resolveTicketName = (t: MemberTicket): string => {
        const populated = (t as any).ticketId;
        if (populated && typeof populated === 'object' && populated.name) return populated.name as string;
        return t.ticketName || '그룹수업 수강권';
    };

    return (
        <Col style={{ gap: 32, padding: '24px 28px' }}>
            {/* ── 수강권 ── */}
            <Col>
                <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Row className="items-center gap-[8px]">
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>그룹수업 수강권</span>
                        <span style={{
                            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                            backgroundColor: '#EFF6FF', color: '#3B82F6',
                        }}>{activeTicketCount}</span>
                        {memberTickets.length !== activeTicketCount && (
                            <span
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    backgroundColor: '#F3F4F6',
                                    color: '#6B7280',
                                }}
                            >
                                전체 {memberTickets.length}
                            </span>
                        )}
                    </Row>
                    {!isReadOnly && <button
                        onClick={() => setShowIssue(true)}
                        style={{
                            padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                            backgroundColor: '#3B82F6', color: 'white', border: 'none', cursor: 'pointer',
                        }}
                    >
                        + 수강권 발급
                    </button>}
                </Row>

                {ticketsLoading ? (
                    <span style={{ fontSize: 13, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
                        <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #3B82F6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        불러오는 중...
                    </span>
                ) : memberTickets.length === 0 ? (
                    <Col style={{ padding: '40px 0', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 32 }}>🎫</span>
                        <span style={{ fontSize: 14, color: '#9CA3AF' }}>그룹수업 수강권이 없습니다</span>
                    </Col>
                ) : (
                    <Row style={{ flexWrap: 'wrap', gap: 20 }}>
                        {memberTickets.map((t: MemberTicket) => {
                            const cfg = STATUS_CFG[t.status] || STATUS_CFG.ACTIVE;
                            const isActive = t.status === 'ACTIVE';
                            const remainingCount = Math.max(Number(t.remainingCount ?? 0), 0);
                            const usedCount = Math.max(Number(t.usedCount ?? 0), 0);
                            const startSessionValue = Math.max(Number(t.startSessionValue ?? 1), 1);
                            const fallbackTotalCount = Math.max(usedCount + remainingCount, startSessionValue + remainingCount - 1);
                            const totalCount = Math.max(Number(t.totalSession ?? 0), fallbackTotalCount, 0);
                            const currentSession = totalCount > 0
                                ? Math.min(Math.max(totalCount - remainingCount, 0), totalCount)
                                : 0;
                            const usagePercent = totalCount > 0
                                ? Math.min(Math.max((currentSession / totalCount) * 100, 0), 100)
                                : 0;
                            const daysLeft = t.expiryDate ? dayjs(t.expiryDate).diff(dayjs(), 'day') : null;
                            const ticketName = resolveTicketName(t);
                            const sessionValue = `${totalCount > 0 ? currentSession : '-'}/${totalCount || '-'}회`;
                            const remainColor = remainingCount <= 2 ? '#EF4444' : '#2563EB';
                            const daysLeftValue = daysLeft === null ? '-' : `${daysLeft}일`;
                            const daysLeftColor =
                                daysLeft === null
                                    ? '#111827'
                                    : daysLeft <= 7
                                    ? '#EF4444'
                                    : daysLeft <= 30
                                    ? '#D97706'
                                    : '#111827';
                            // 공유받은 티켓은 소유자의 것이다. 표시와 수업 등록만 허용하고
                            // 소유자 액션(상세 진입 → 환불·삭제 등)으로 들어가지 않게 한다.
                            const isSharedTicket = t.access?.type === 'SHARED';
                            // 정지 배지가 붙으면 머리 높이가 한 줄 늘어난다 — 고정 높이면 잘린다.
                            const hasPauseBadge = !!getPauseBadge(t as any);
                            const sharedOwnerName =
                                (t.access?.type === 'SHARED' ? t.access.ownerName : '') || '소유자';
                            const canRegister = !isReadOnly && isActive && remainingCount > 0;
                            const isRegisterButtonHover = hoveredRegisterTicketId === t._id;
                            const isRegisterButtonActive = activeRegisterTicketId === t._id;
                            const registerButtonStyle: React.CSSProperties = {
                                height: 30,
                                marginLeft: 0,
                                padding: '0 10px',
                                borderRadius: 7,
                                border: `1px solid ${
                                    isRegisterButtonActive
                                        ? '#34D399'
                                        : isRegisterButtonHover
                                        ? '#6EE7B7'
                                        : '#A7F3D0'
                                }`,
                                backgroundColor: isRegisterButtonActive
                                    ? '#A7F3D0'
                                    : isRegisterButtonHover
                                    ? '#D1FAE5'
                                    : '#ECFDF5',
                                color: '#047857',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                boxShadow: isRegisterButtonHover ? '0 6px 14px rgba(5, 150, 105, 0.16)' : 'none',
                                transform: isRegisterButtonHover && !isRegisterButtonActive ? 'translateY(-1px)' : 'translateY(0)',
                                transition:
                                    'background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease',
                            };

                            return (
                                <Col
                                    key={t._id}
                                    title={
                                        isSharedTicket
                                            ? `${sharedOwnerName}님의 수강권입니다. 관리는 소유자 프로필에서 하세요.`
                                            : undefined
                                    }
                                    onClick={() => {
                                        /**
                                         * 공유받은 티켓의 상세는 소유자의 수강권 상세다. 여기서 열면 환불·삭제 같은
                                         * 소유자 액션을 남의 수강권에 실행할 수 있어 소유자 프로필로 보낸다.
                                         *
                                         * 예전에는 여기서 알럿을 한 번 띄웠는데, 눌러 본 뒤에야 "못 연다"고
                                         * 알려주는 막다른 길이었다. 카드가 이미 '공유 · ○○님'과 '소유자
                                         * 프로필에서 관리'를 달고 있으므로 확인 단계 없이 바로 이동한다.
                                         */
                                        if (isSharedTicket) {
                                            const ownerMemberId =
                                                t.access?.type === 'SHARED' ? t.access.ownerMemberId : '';
                                            if (!ownerMemberId) {
                                                createAlert(
                                                    '소유자를 확인할 수 없습니다',
                                                    '공유받은 수강권인데 소유자 정보를 불러오지 못했습니다.\n새로고침 후에도 같다면 고객센터로 문의해주세요.',
                                                    true,
                                                );
                                                return;
                                            }
                                            // 새 탭으로 연다. 같은 탭이면 보고 있던 회원 프로필 맥락을 잃는다.
                                            window.open(
                                                `/publicMember/${ownerMemberId}`,
                                                '_blank',
                                                'noopener,noreferrer',
                                            );
                                            return;
                                        }
                                        navigate('/member/detail/membership', {
                                            state: {
                                                membershipId: t._id,
                                                memberId,
                                                memberName,
                                                coachName: '',
                                                coachId: '',
                                                source: 'group',
                                                centerId,
                                            },
                                        });
                                    }}
                                    style={{
                                        width: 316,
                                        // 공유 카드는 '소유자 프로필에서 관리' 줄이 한 줄 더 붙는다.
                                        // 고정 160px이면 overflow:hidden에 잘려 안내가 사라진다.
                                        height: isSharedTicket || hasPauseBadge ? 'auto' : 160,
                                        minHeight: 160,
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        boxShadow: '2px 2px 12px 0px #00000029',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        transition: 'transform 0.15s',
                                    }}
                                    onMouseEnter={(e: any) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
                                    onMouseLeave={(e: any) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                >
                                    {/* 상단 — 그라디언트 헤더 */}
                                    <Col
                                        style={{
                                            background: isActive
                                                ? 'linear-gradient(135deg, #10B981 0%, #065F46 100%)'
                                                : '#D1D5DB',
                                            height: isSharedTicket || hasPauseBadge ? 'auto' : 84,
                                            minHeight: 84,
                                            padding: '16px 24px',
                                            justifyContent: 'center',
                                            gap: 10,
                                        }}
                                    >
                                        <Row style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <span style={{
                                                fontSize: 15, fontWeight: 700, color: 'white',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                maxWidth: 180,
                                            }}>
                                                {ticketName}
                                            </span>
                                            <span style={{
                                                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                                                backgroundColor: 'rgba(255,255,255,0.22)', color: '#FFFFFF',
                                            }}>
                                                그룹
                                            </span>
                                            <span style={{
                                                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                                                backgroundColor: cfg.bg, color: cfg.color,
                                            }}>
                                                {cfg.label}
                                            </span>
                                            {isSharedTicket && (
                                                <span
                                                    title={`${sharedOwnerName}님의 수강권을 공유받아 사용합니다`}
                                                    style={{
                                                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                                                        backgroundColor: 'rgba(255,255,255,0.9)', color: '#2B529D',
                                                    }}
                                                >
                                                    공유 · {sharedOwnerName}
                                                </span>
                                            )}
                                        </Row>
                                        <MembershipPauseBadge pass={t as any} variant="onColor" className="self-start" />
                                        <Row style={{ alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
                                                ~{t.expiryDate ? dayjs(t.expiryDate).format('YYYY.MM.DD') : '-'}
                                            </span>
                                            {daysLeft !== null && isActive && (
                                                <span style={{
                                                    fontSize: 11, fontWeight: 600,
                                                    color: daysLeft <= 7 ? '#FCA5A5' : daysLeft <= 30 ? '#FDE68A' : 'rgba(255,255,255,0.85)',
                                                }}>
                                                    {daysLeft > 0 ? `D-${daysLeft}` : '오늘 만료'}
                                                </span>
                                            )}
                                        </Row>
                                        {/*
                                          누르기 전에 무슨 일이 일어나는지 카드가 먼저 말한다.
                                          클릭 후 알럿으로 "여기서는 못 고친다"고 알려주면 이미 헛클릭이다.
                                        */}
                                        {isSharedTicket && (
                                            // 평문이면 라벨로 읽혀 아무도 누를 수 있는 줄 모른다. 눌리는 모양을 준다.
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    width: 'fit-content',
                                                    padding: '4px 8px',
                                                    borderRadius: 6,
                                                    border: '1px solid rgba(255,255,255,0.42)',
                                                    backgroundColor: 'rgba(255,255,255,0.14)',
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    color: '#FFFFFF',
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                소유자 프로필에서 관리
                                                <ExternalLink size={11} />
                                            </span>
                                        )}
                                    </Col>

                                    {/* 하단 — 회차 정보 */}
                                    <Col style={{
                                        height: 76,
                                        backgroundColor: '#FAFAFC',
                                        padding: '10px 18px 12px',
                                        justifyContent: 'center',
                                    }}>
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '92px minmax(0, 1fr)',
                                                alignItems: 'center',
                                                columnGap: 12,
                                                width: '100%',
                                            }}
                                        >
                                            <Col style={{ gap: 2, minWidth: 0 }}>
                                                <span style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 500 }}>진행 회차</span>
                                                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827', lineHeight: '18px' }}>
                                                    {sessionValue}
                                                </span>
                                                <span style={{ fontSize: 11, lineHeight: '14px', fontWeight: 700, color: remainColor }}>
                                                    잔여 {remainingCount}회
                                                </span>
                                            </Col>
                                            <Col style={{ gap: 8, minWidth: 0 }}>
                                                <Row style={{ alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                                    <Col style={{ gap: 2, minWidth: 0 }}>
                                                        <span style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 500 }}>유효 기간</span>
                                                        <span style={{ fontSize: 14, fontWeight: 700, color: daysLeftColor, lineHeight: '18px' }}>
                                                            {daysLeftValue}
                                                        </span>
                                                    </Col>
                                                    {canRegister && (
                                                        <button
                                                            type="button"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                setRegisterTarget(t);
                                                            }}
                                                            onMouseEnter={() => setHoveredRegisterTicketId(t._id)}
                                                            onMouseLeave={() => {
                                                                setHoveredRegisterTicketId(null);
                                                                setActiveRegisterTicketId(null);
                                                            }}
                                                            onMouseDown={() => setActiveRegisterTicketId(t._id)}
                                                            onMouseUp={() => setActiveRegisterTicketId(null)}
                                                            style={registerButtonStyle}
                                                        >
                                                            <CalendarPlus size={14} />
                                                            수업 등록
                                                        </button>
                                                    )}
                                                </Row>
                                                <div style={{ height: 4, backgroundColor: '#E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
                                                    <div style={{
                                                        width: `${usagePercent}%`, height: '100%',
                                                        backgroundColor: isActive ? '#10B981' : '#9CA3AF',
                                                        borderRadius: 2, transition: 'width 0.3s ease',
                                                    }} />
                                                </div>
                                            </Col>
                                        </div>
                                    </Col>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </Col>

            {/* ── 예약 이력 (탭 분류 적용) ── */}
            <Col>
                <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <Row className="items-center gap-[12px]">
                        <Row className="items-center gap-[8px]">
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>예약 이력</span>
                            <span style={{
                                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                                backgroundColor: '#F3F4F6', color: '#6B7280',
                            }}>{bookings.length}</span>
                        </Row>
                        {/* 숨기기 체크박스 */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', paddingLeft: 8 }}>
                            <input 
                                type="checkbox" 
                                checked={hideCancelled}
                                onChange={(e) => setHideCancelled(e.target.checked)}
                                style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#3B82F6' }}
                            />
                            <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>취소 내역 숨기기</span>
                        </label>
                    </Row>
                    {/* 탭 컨트롤 (Segmented Control) */}
                    <Row style={{ backgroundColor: '#F3F4F6', padding: 4, borderRadius: 8, gap: 4 }}>
                        <button
                            onClick={() => setBookingTab('upcoming')}
                            style={{
                                padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                                border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                                backgroundColor: bookingTab === 'upcoming' ? 'white' : 'transparent',
                                color: bookingTab === 'upcoming' ? '#111827' : '#6B7280',
                                boxShadow: bookingTab === 'upcoming' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            }}
                        >
                            예정된 예약 <span style={{ color: bookingTab === 'upcoming' ? '#3B82F6' : '#9CA3AF' }}>{upcomingBookings.length}</span>
                        </button>
                        <button
                            onClick={() => setBookingTab('past')}
                            style={{
                                padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                                border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                                backgroundColor: bookingTab === 'past' ? 'white' : 'transparent',
                                color: bookingTab === 'past' ? '#111827' : '#6B7280',
                                boxShadow: bookingTab === 'past' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            }}
                        >
                            지난 이력 <span style={{ color: bookingTab === 'past' ? '#3B82F6' : '#9CA3AF' }}>{pastBookings.length}</span>
                        </button>
                    </Row>
                </Row>
                {bookingsLoading ? (
                    <span style={{ fontSize: 13, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
                        <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #3B82F6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        불러오는 중...
                    </span>
                ) : displayedBookings.length === 0 ? (
                    <Col style={{ padding: '40px 0', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 32 }}>📋</span>
                        <span style={{ fontSize: 14, color: '#9CA3AF' }}>
                            {bookingTab === 'upcoming' ? '예정된 예약이 없습니다' : '지난 예약 이력이 없습니다'}
                        </span>
                    </Col>
                ) : (
                    <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ backgroundColor: '#F9FAFB' }}>
                                    <th style={thStyle}>날짜</th>
                                    <th style={thStyle}>수업명</th>
                                    <th style={thStyle}>강사</th>
                                    <th style={thStyle}>수강권</th>
                                    <th style={thStyle}>상태</th>
                                    <th style={thStyle}>예약자</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(groupedBookings).map(([month, monthBookings], idx) => {
                                    const expanded = isMonthExpanded(month, idx);
                                    return (
                                        <React.Fragment key={month}>
                                            {/* 월별 구분선 (Accordion Header) */}
                                            <tr onClick={() => toggleMonth(month)} style={{ cursor: 'pointer' }}>
                                                <td colSpan={6} style={{ 
                                                    padding: '12px 14px', 
                                                    backgroundColor: '#F8FAFC', 
                                                    color: '#374151', 
                                                    fontWeight: 700, 
                                                    fontSize: 13,
                                                    borderTop: '1px solid #E5E7EB',
                                                    borderBottom: '1px solid #E5E7EB',
                                                    transition: 'background-color 0.2s',
                                                }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F1F5F9' }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F8FAFC' }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span>📅 {month}</span>
                                                            <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, backgroundColor: '#E2E8F0', padding: '2px 8px', borderRadius: 10 }}>총 {monthBookings.length}건</span>
                                                        </div>
                                                        <div style={{ color: '#9CA3AF', fontSize: 10, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                                                            ▼
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expanded && monthBookings.map((b) => {
                                                const bCfg = BOOKING_STATUS_CFG[b.status] || BOOKING_STATUS_CFG.CONFIRMED;
                                                const isCancelled = b.status === 'CANCELLED';
                                                
                                                return (
                                                    <tr key={b._id} style={{ 
                                                        borderBottom: '1px solid #F3F4F6', 
                                                        backgroundColor: '#FFFFFF',
                                                        opacity: isCancelled ? 0.4 : 1, // 취소된 내역은 흐리게 처리
                                                        transition: 'opacity 0.2s',
                                                    }}>
                                                        <td style={{...tdStyle, textDecoration: isCancelled ? 'line-through' : 'none', color: isCancelled ? '#9CA3AF' : tdStyle.color}}>
                                                            {dayjs(b.startTime).format('DD일 (ddd)')}
                                                            <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 6 }}>
                                                                {dayjs(b.startTime).format('HH:mm')}~{dayjs(b.endTime).format('HH:mm')}
                                                            </span>
                                                        </td>
                                                        <td style={{ ...tdStyle, fontWeight: 600 }}>{b.sessionTitle}</td>
                                                        <td style={tdStyle}>{b.coachName || '-'}</td>
                                                        <td style={tdStyle}>{b.ticketName || '-'}</td>
                                                        <td style={tdStyle}>
                                                            <span style={{
                                                                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                                                                backgroundColor: bCfg.bg, color: bCfg.color,
                                                            }}>{bCfg.label}</span>
                                                        </td>
                                                        <td style={tdStyle}>
                                                            <span style={{ fontSize: 12, color: '#6B7280' }}>
                                                                {b.bookedBy === 'member' ? '본인' : b.bookedBy === 'admin' ? '관리자' : '강사'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Col>


            {/* 수강권 발급 모달 */}
            {!isReadOnly && showIssue && (
                <ModalMembershipCreate
                    setIsVisible={setShowIssue}
                    onClickCreateNewMembership={() => {
                        setShowIssue(false);
                        navigate('/classPass', { state: { initialTab: 'group', initialSub: 'tickets' } });
                    }}
                    memberId={memberId}
                    memberName={memberName}
                    createAlert={createAlert}
                    source="group"
                    centerId={centerId}
                />
            )}
            {!isReadOnly && registerTarget && (
                <ModalGroupTicketSessionRegister
                    setIsVisible={(visible) => {
                        const nextVisible = typeof visible === 'function' ? visible(true) : visible;
                        if (!nextVisible) setRegisterTarget(null);
                    }}
                    centerId={centerId}
                    memberId={memberId}
                    memberName={memberName}
                    membership={registerTarget}
                    createAlert={createAlert}
                />
            )}
        </Col>
    );
};

export default MemberGroupTab;
